import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Navigate } from '@sorare/routing';
import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { League } from '@sorare/core/src/components/landing/types';
import {
  ANY_SPORT_HOME,
  FOOTBALL_ONBOARDING,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';

import { WalletLanding } from '#src/components/landing/WalletLanding/index.tsx';

import { winAssets } from './assets/assets.ts';
import fieldAvif from './laliga/assets/play/field.avif';
import lineupAvif from './laliga/assets/play/lineup.avif';
import lineup from './laliga/assets/play/lineup.webp';
import field from './ligue-1/assets/play/field.webp';
import scoutBgAvif from './ligue-1/assets/scout/background.avif';
import scoutBg from './ligue-1/assets/scout/background.webp';
import playersAvif from './ligue-1/assets/scout/players.avif';
import players from './ligue-1/assets/scout/players.webp';
import bgAvif from './premier-league/assets/hero/bg.avif';
import bg from './premier-league/assets/hero/bg.webp';
import leftSidePlayersAvif from './premier-league/assets/hero/players/leftSidePlayers.avif';
import leftSidePlayers from './premier-league/assets/hero/players/leftSidePlayers.webp';
import rightSidePlayersAvif from './premier-league/assets/hero/players/rightSidePlayers.avif';
import rightSidePlayers from './premier-league/assets/hero/players/rightSidePlayers.webp';

const messages = defineMessages({
  metadatasTitle: {
    id: 'Football.Home.Public.meta.title',
    defaultMessage:
      'Play fantasy football with ownable digital player cards - Sorare',
  },
  metadatasDescription: {
    id: 'Football.Home.Public.meta.description',
    defaultMessage:
      "Play Sorare's free fantasy football game. Collect, buy, sell & compete with ownable digital player cards from the world's top leagues to win cash prizes.",
  },
});

const heroAssets = {
  background: {
    avif: bgAvif,
    pictureUrl: bg,
  },
  players: [
    {
      avif: leftSidePlayersAvif,
      pictureUrl: leftSidePlayers,
      height: 509,
      width: 561,
    },
    {
      avif: rightSidePlayersAvif,
      pictureUrl: rightSidePlayers,
      height: 510,
      width: 540,
    },
  ],
};

const scoutAssets = {
  background: {
    avif: scoutBgAvif,
    pictureUrl: scoutBg,
  },
  players: {
    avif: playersAvif,
    pictureUrl: players,
  },
};

const playAssets = {
  background: {
    avif: fieldAvif,
    pictureUrl: field,
  },
  lineup: {
    avif: lineupAvif,
    pictureUrl: lineup,
  },
};

type Props = {
  isRoot?: boolean;
};
const FootballPage = ({ isRoot }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { currentUser: contextCurrentUser, footballUserProfile } =
    useCurrentUserContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: 'https://frontend-assets.sorare.com/meta/social-picture-football-2026.jpg',
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );
  if (isLoggedIn) {
    if (
      contextCurrentUser?.forcedIntoOnboarding &&
      !footballUserProfile?.commonGameOnboarded
    ) {
      return <Navigate to={FOOTBALL_ONBOARDING} replace />;
    }
    return (
      <Navigate
        to={generateSportContextPath(ANY_SPORT_HOME)}
        replace
        keepSearchParams
      />
    );
  }

  return (
    <WalletLanding
      assets={{
        heroAssets,
        scoutAssets,
        playAssets,
        winAssets,
      }}
      leaderboards={[
        So5LeaderboardType.IN_SEASON_ENGLAND_LIMITED,
        So5LeaderboardType.IN_SEASON_FRANCE_LIMITED,
        So5LeaderboardType.IN_SEASON_GERMANY_LIMITED,
      ]}
      showFAQ={!isRoot}
      league={League.Global}
    />
  );
};

export default FootballPage;
